import { useEffect, useState } from 'react';
import { Season, TaskTags, Task } from '@common-types';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import { FormControl, InputLabel, MenuItem, SelectChangeEvent, TextField } from '@mui/material';
import { CoreDialog, CoreBox, CoreTextField, CoreTypography } from '@youscience/core';

import { SEASON_TABS_DATA } from '@constants/educationPlanner';
import DialogFooter from '@components/DialogFooter';
import useEducationPlannerStore from '@store/EducationPlannerStore';

import {
  sxStyles,
  StyledDialogTitle,
  StyledDialogContent,
  StyledDatePicker,
  StyledSelect,
  MenuPropsStyles,
  StyledDialogFormControls,
} from './CustomTask.styles';
import ActionButton from '@components/ActionButton';

interface CustomTaskProps {
  isOpenTask: boolean;
  onCloseTask: () => void;
}

const titleCount = 50;
const descriptionCount = 500;
const mask = '__/__/____';

interface FormState {
  title: string;
  description: string;
  dueDate: Date | null;
  linkUrls: { id: number; url: string; isError: boolean; errorText: string }[];
  grade: number;
  season: Season;
  tag: string;
  descriptionHelperText?: string;
  titleHelperText?: string;
}

const invalidUrlMessage = 'Please enter a URL in the format http(s)://www.example.com';
const duplicateUrlMessage = 'Please enter a unique URL';

const ValidGrades = [
  { grade: 9, description: 'Grade 9' },
  { grade: 10, description: 'Grade 10' },
  { grade: 11, description: 'Grade 11' },
  { grade: 12, description: 'Grade 12' },
] as const;

export const CustomTask = (props: CustomTaskProps) => {
  const [isReadyToSave, setIsReadyToSave] = useState(false);

  const { isOpenTask, onCloseTask } = props;

  const [popperOpen, setPopperOpen] = useState(false);
  const [datePickerInError, setDatePickerInError] = useState(false);

  const { currentGrade, currentSeason, currentTask } = useEducationPlannerStore((state) => state.educationPlannerData);
  const { setEducationPlannerData } = useEducationPlannerStore((state) => state);

  const addCustomTask = useEducationPlannerStore((state) => state.addCustomTask);
  const editCustomTask = useEducationPlannerStore((state) => state.editCustomTask);

  let initialState: FormState = {
    title: '',
    description: '',
    dueDate: null,
    linkUrls: [{ id: 0, url: '', isError: false, errorText: '' }],
    grade: currentGrade > 0 ? currentGrade : ValidGrades[0].grade,
    season: currentSeason as Season,
    tag: '-1',
    descriptionHelperText: '',
    titleHelperText: '',
  };

  if (currentTask) {
    initialState = {
      ...initialState,
      title: currentTask.title,
      description: currentTask.description,
      dueDate: currentTask.dueDate ? new Date(currentTask.dueDate) : null,
      grade: currentTask.grades[0],
      season: currentTask.season,
    };

    if (currentTask.linkUrls && currentTask.linkUrls.length > 0) {
      initialState = {
        ...initialState,
        linkUrls: currentTask.linkUrls.map((linkUrl, index) => ({
          id: index,
          url: linkUrl,
          isError: false,
          errorText: '',
        })),
      };
    }

    if (currentTask.tags?.length) {
      initialState = {
        ...initialState,
        tag: currentTask.tags[0],
      };
    }
  }

  const [formState, setFormState] = useState<FormState>(initialState);

  const updateFormValues = (fieldUpdates: Partial<FormState>) => {
    setFormState((prevState) => ({
      ...prevState,
      ...fieldUpdates,
    }));
  };

  useEffect(() => {
    if (currentTask) {
      return;
    }

    updateFormValues({ season: currentSeason as Season });
  }, [currentSeason]);

  useEffect(() => {
    if (currentTask) {
      return;
    }

    updateFormValues({ grade: currentGrade > 0 ? currentGrade : ValidGrades[0].grade });
  }, [currentGrade]);

  const handleGradeChange = (value: SelectChangeEvent<unknown>) => {
    updateFormValues({ grade: value.target.value as number });
  };

  const handleTagChange = (value: SelectChangeEvent<unknown>) => {
    updateFormValues({ tag: value.target.value as string });
  };

  const handleSeasonChange = (value: SelectChangeEvent<unknown>) => {
    updateFormValues({ season: value.target.value as Season });
  };

  const handleAddLink = () => {
    setFormState((prevState) => ({
      ...prevState,
      linkUrls: [...prevState.linkUrls, { id: prevState.linkUrls.length, url: '', isError: false, errorText: '' }],
    }));
  };

  useEffect(() => {
    let isValid = formState.title.length > 0;

    if (isValid) {
      isValid = formState.linkUrls.every((linkError) => !linkError.isError);
    }

    setIsReadyToSave(isValid);
  }, [formState.title, formState.linkUrls]);

  const handleCancelTask = () => {
    if (currentTask) {
      setEducationPlannerData({ currentTask: undefined });
    }

    updateFormValues(initialState);
    onCloseTask();
  };

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    let helperText = '';

    if (value.length > 0) {
      helperText = `(${value.length}/${titleCount})`;
    }

    updateFormValues({ titleHelperText: helperText, title: value });
  };

  const handleDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    let helperText = '';

    if (value.length > 0) {
      helperText = `(${value.length}/${descriptionCount})`;
    }

    updateFormValues({ descriptionHelperText: helperText, description: value });
  };

  const handleDateChange = (value: Date | null) => {
    updateFormValues({ dueDate: value });
  };

  const handleSaveTask = () => {
    if (formState.linkUrls.find((linkError) => linkError.isError)) return;

    const data: Partial<Task> = {
      title: formState.title,
      description: formState.description,
      dueDate: formState.dueDate ? formatDate(formState.dueDate) : '',
      linkUrls: formState.linkUrls.reduce((acc, link) => {
        if (link.url) acc.push(link.url);

        return acc;
      }, [] as string[]),
      grades: [formState.grade],
      season: formState.season,
      tags: [],
    };

    if (formState.tag && formState.tag !== '-1') {
      data.tags = [formState.tag as TaskTags];
    }

    if (currentTask?._id) {
      editCustomTask({ ...data, _id: currentTask._id });
    } else {
      addCustomTask(data);
    }

    updateFormValues(initialState);
    onCloseTask();
  };

  const handleUrlChange = (urlValue: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, id: number) => {
    const { value } = urlValue.target;

    const linkId = Number(id);
    const newLinkUrls = [...formState.linkUrls];

    newLinkUrls[linkId].url = value;

    updateFormValues({ linkUrls: newLinkUrls });
  };

  const isLinkError = (id: number) => {
    const linkId = Number(id);
    const linkError = formState.linkUrls.find((link) => link.id === linkId);

    return linkError?.isError;
  };

  const getLinkErrorText = (id: number) => {
    const linkId = Number(id);
    const linkError = formState.linkUrls.find((link) => link.id === linkId);

    return linkError?.errorText;
  };

  const validateUrlChange = (urlValue: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, id: number) => {
    const regex = /(?:https?):\/\/(\w+:?\w*)?(\S+)(:\d+)?(\/|\/([\w#!:.?+=&%!\-/]))?/;
    const { value } = urlValue.target;

    const linkId = Number(id);
    const newLinkErrors = [...formState.linkUrls];

    if (!value) {
      newLinkErrors[linkId].isError = false;
    } else {
      newLinkErrors[linkId].isError = !regex.test(value);

      if (newLinkErrors[linkId].isError) {
        newLinkErrors[linkId].errorText = invalidUrlMessage;
      }

      // ensure that the link is not duplicated
      const linkUrls = formState.linkUrls.map((link) => link.url);
      const isDuplicated = linkUrls.filter((link) => link === value).length > 1;

      if (isDuplicated) {
        newLinkErrors[linkId].isError = true;
        newLinkErrors[linkId].errorText = duplicateUrlMessage;
      }
    }

    updateFormValues({ linkUrls: newLinkErrors });
  };

  return (
    <CoreDialog sx={sxStyles.dialogWrap} open={isOpenTask} aria-labelledby="task-title">
      <StyledDialogTitle id="task-title">Customize your task</StyledDialogTitle>

      <StyledDialogContent data-testid="surveyContent">
        <CoreTypography sx={sxStyles.subtitle}>
          Do you have an extra assignment? You can customize your task and add one into the roadmap.
        </CoreTypography>

        <StyledDialogFormControls>
          <CoreBox sx={sxStyles.formControlItem}>
            <FormControl fullWidth>
              <InputLabel required>Grade</InputLabel>

              <StyledSelect
                data-testid="gradeSelect"
                MenuProps={MenuPropsStyles}
                label="Grade"
                onChange={handleGradeChange}
                value={formState.grade}
              >
                {ValidGrades.map((currentGrade) => (
                  <MenuItem key={currentGrade.grade} value={currentGrade.grade}>
                    {currentGrade.description}
                  </MenuItem>
                ))}
              </StyledSelect>
            </FormControl>
          </CoreBox>

          <CoreBox sx={sxStyles.formControlItem}>
            <FormControl fullWidth>
              <InputLabel>Tag</InputLabel>

              <StyledSelect
                data-testid="typeSelect"
                MenuProps={MenuPropsStyles}
                label="Tag"
                onChange={handleTagChange}
                defaultValue=""
                value={formState.tag}
              >
                <MenuItem key={-1} value="-1">
                  No tags
                </MenuItem>

                {Object.keys(TaskTags).map((key) => {
                  return (
                    <MenuItem key={key} value={(TaskTags as Record<string, string>)[key]}>
                      {(TaskTags as Record<string, string>)[key]}
                    </MenuItem>
                  );
                })}
              </StyledSelect>
            </FormControl>
          </CoreBox>

          <CoreBox sx={sxStyles.formControlItem}>
            <FormControl fullWidth>
              <InputLabel required>Season</InputLabel>

              <StyledSelect
                data-testid="seasonSelect"
                MenuProps={MenuPropsStyles}
                label="season"
                onChange={handleSeasonChange}
                value={formState.season}
              >
                {Object.values(SEASON_TABS_DATA).map(({ mode, title, subtitle }) => {
                  return (
                    <MenuItem key={mode} value={title}>
                      {title} ({subtitle})
                    </MenuItem>
                  );
                })}
              </StyledSelect>
            </FormControl>
          </CoreBox>

          <CoreBox sx={sxStyles.formControlItem}>
            <FormControl fullWidth data-testid="dueDate">
              <StyledDatePicker
                views={['year', 'month', 'day']}
                label="Due date"
                mask={mask}
                onChange={handleDateChange}
                onClose={() => setPopperOpen(false)}
                onOpen={() => setPopperOpen(true)}
                PopperProps={{
                  open: popperOpen,
                  placement: 'bottom-start',
                }}
                slots={{
                  textField: TextField,
                }}
                value={formState.dueDate}
                onError={(error) => setDatePickerInError(error !== null)}
              />
            </FormControl>
          </CoreBox>
        </StyledDialogFormControls>

        <CoreBox>
          <CoreTypography sx={sxStyles.subtitle} variant="h6" component="h5">
            ABOUT TASK
          </CoreTypography>

          <CoreBox sx={sxStyles.formControlItems}>
            <FormControl fullWidth>
              <CoreTextField
                data-testid="titleInput"
                sx={sxStyles.formHelperText}
                required
                label="Title"
                inputProps={{ maxLength: titleCount }}
                onChange={handleTitleChange}
                helperText={formState.titleHelperText}
                value={formState.title}
              />
            </FormControl>

            <FormControl fullWidth>
              <TextField
                label="Description"
                inputProps={{ maxLength: descriptionCount }}
                onChange={handleDescriptionChange}
                helperText={formState.descriptionHelperText}
                value={formState.description}
                multiline
                rows={3}
                sx={sxStyles.formHelperText}
              />
            </FormControl>
          </CoreBox>
        </CoreBox>

        <CoreBox sx={sxStyles.addResourceWrap}>
          <CoreTypography sx={sxStyles.subtitle} variant="h6" component="h5">
            ADD EXTERNAL RESOURCE(S)
          </CoreTypography>

          <CoreBox sx={sxStyles.formControlItems}>
            {formState.linkUrls.map((link) => {
              return (
                <FormControl fullWidth key={link.id}>
                  <CoreTextField
                    label="Link"
                    type="url"
                    onBlur={(e) => validateUrlChange(e, link.id)}
                    onChange={(e) => handleUrlChange(e, link.id)}
                    endAdornment={<InsertLinkIcon />}
                    error={isLinkError(link.id)}
                    helperText={isLinkError(link.id) ? getLinkErrorText(link.id) : ' '}
                    value={link.url}
                  />
                </FormControl>
              );
            })}

            {formState.linkUrls.length < 5 && (
              <ActionButton variant="outlined" onClick={handleAddLink}>
                Add another resource +
              </ActionButton>
            )}
          </CoreBox>
        </CoreBox>
      </StyledDialogContent>

      <DialogFooter
        goBackButtonTitle="Cancel"
        goNextButtonTitle="Save"
        goNextActionButtonTitle="Saving"
        isDisableNextButton={!isReadyToSave || datePickerInError}
        onGoBack={handleCancelTask}
        onGoNext={handleSaveTask}
      />
    </CoreDialog>
  );
};
